import {
  Lock, ShieldAlert, MapPin,
  Layers, Bath, DoorOpen, ChefHat, Paintbrush, Home, Leaf,
  TrendingUp, Wrench, Warehouse,
} from "lucide-react";

const STRAT_ICON = {
  category_shift_bedroom: DoorOpen,
  internal_reconfiguration: Layers,
  kitchen_reconfiguration: ChefHat,
  bathroom_additional: Bath,
  bathroom_ensuite: Bath,
  garage_income_asset: Warehouse,
  secondary_space_separation: DoorOpen,
  cosmetic_perception: Paintbrush,
  curb_appeal: Home,
  energy_future_proofing: Leaf,
};

const KEY_CONSIDERATION = {
  category_shift_bedroom: "Focus on reclaiming underused space — a dining room, study, or loft can transform value.",
  internal_reconfiguration: "A good architect can maximise flow without moving services.",
  kitchen_reconfiguration: "Mid-range specification often returns more than luxury finishes.",
  bathroom_additional: "Plumbing proximity to existing services keeps costs down.",
  bathroom_ensuite: "Compact layouts can work well — doesn't need to be large.",
  garage_income_asset: "Check permitted development rights — many garage conversions don't need planning.",
  secondary_space_separation: "Insulation and lighting are key to making converted space feel permanent.",
  cosmetic_perception: "Neutral tones and consistent flooring create the biggest visual impact.",
  curb_appeal: "The front door and driveway set the tone for every viewing.",
  energy_future_proofing: "Check for available grants before starting — they can offset 30-50% of cost.",
};

function getValueRating(s) {
  const pct = s.percentage_range;
  if (!pct) return "Med";
  const avg = (pct.lower + pct.upper) / 2;
  if (avg >= 12) return "High";
  if (avg >= 6) return "Med";
  return "Low";
}

function getEffortRating(s) {
  const impl = s.implementation_range;
  if (!impl) return "Med";
  const avg = (impl.lower + impl.upper) / 2;
  if (avg >= 20000) return "High";
  if (avg >= 10000) return "Med";
  return "Low";
}

function ratingColor(rating) {
  if (rating === "High") return "text-cipher-green bg-cipher-green/15";
  if (rating === "Med") return "text-cipher-gold bg-cipher-gold/15";
  return "text-amber-400 bg-amber-400/15";
}

function effortColor(rating) {
  if (rating === "Low") return "text-cipher-green bg-cipher-green/15";
  if (rating === "Med") return "text-cipher-gold bg-cipher-gold/15";
  return "text-amber-400 bg-amber-400/15";
}

export function WhatItMeans({
  valueHeadroom,
  isUnlocked,
  ceilingCaution,
  currentScore,
  comparisonContext,
  valueHeadroomRange,
}) {
  if (!valueHeadroom?.range) return null;

  const { strategies, supportive_strategies, value_traps } = valueHeadroom;
  const allStrategies = [...(strategies || []), ...(supportive_strategies || [])];
  const trapCount = value_traps?.length || 0;

  const potentialScore = Math.min(
    (currentScore || 60) + Math.min(allStrategies.length * 4, 20),
    95
  );

  // Max percentage for bar scaling
  const maxPct = Math.max(
    ...allStrategies.map(s => s.percentage_range?.upper || 10),
    15
  );

  return (
    <div data-testid="section-what-it-means" className="space-y-3">
      {/* Comparison context */}
      {comparisonContext && (
        <div className="flex items-start gap-2.5 bg-white/[0.03] rounded-xl px-3.5 py-2.5" data-testid="comparison-context">
          <MapPin className="w-3.5 h-3.5 text-cipher-gold/50 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-white/50 leading-relaxed">{comparisonContext}</p>
        </div>
      )}

      {/* Strategy cards with % bars, value/effort ratings */}
      <div className="space-y-2" data-testid="strategy-cards">
        {allStrategies.slice(0, 5).map((s) => {
          const Icon = STRAT_ICON[s.id] || Layers;
          const pctRange = s.percentage_range;
          const avgPct = pctRange ? Math.round((pctRange.lower + pctRange.upper) / 2) : null;
          const barWidth = pctRange ? Math.round((pctRange.upper / maxPct) * 100) : 0;
          const valueR = getValueRating(s);
          const effortR = getEffortRating(s);
          const consideration = KEY_CONSIDERATION[s.id];
          const isFootprintFirst = s.footprint_first !== false;

          return (
            <div
              key={s.id}
              className="bg-white/[0.04] rounded-xl px-3.5 py-3 space-y-2"
              data-testid={`strategy-card-${s.id}`}
            >
              {/* Title row */}
              <div className="flex items-center gap-2.5">
                <Icon className="w-4 h-4 text-cipher-green/60 flex-shrink-0" />
                <span className="text-sm font-medium text-white/85 flex-1">{s.label}</span>
                {!isFootprintFirst && (
                  <span className="text-[8px] uppercase tracking-wider text-amber-400/60 bg-amber-400/10 px-1.5 py-0.5 rounded">
                    Extension
                  </span>
                )}
              </div>

              {/* Percentage uplift bar */}
              {pctRange && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white/35">Potential uplift to property value</span>
                    <span className="text-xs font-semibold text-cipher-green">
                      {pctRange.lower}–{pctRange.upper}%
                    </span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cipher-green/60 to-cipher-green rounded-full transition-all duration-700"
                      style={{ width: `${barWidth}%` }}
                      data-testid={`strategy-bar-${s.id}`}
                    />
                  </div>
                </div>
              )}

              {/* Key consideration */}
              {consideration && (
                <p className="text-[11px] text-white/35 leading-relaxed pl-6.5">
                  {consideration}
                </p>
              )}

              {/* Value + Effort + Technicality ratings */}
              <div className="flex items-center gap-2 pl-6.5 flex-wrap">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-white/25" />
                  <span className="text-[9px] text-white/30">Value:</span>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${ratingColor(valueR)}`}>{valueR}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Wrench className="w-3 h-3 text-white/25" />
                  <span className="text-[9px] text-white/30">Effort:</span>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${effortColor(effortR)}`}>{effortR}</span>
                </div>
                {s.technicality && (
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                    s.technicality === 'DIY-friendly' ? 'text-cipher-green bg-cipher-green/15' :
                    s.technicality === 'Professional' ? 'text-cipher-gold bg-cipher-gold/15' :
                    'text-blue-400 bg-blue-400/15'
                  }`}>{s.technicality}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
